// src/pages/ConfirmEmail.js
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./Login.css";

export default function ConfirmEmail() {
  const [status, setStatus] = useState("verifying");
  const [errorMsg, setErrorMsg] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    async function handleVerification() {
      const token = searchParams.get("token");
      const uid = searchParams.get("uid");

      if (!token || !uid) {
        setStatus("error");
        setErrorMsg("Invalid verification link.");
        return;
      }

      try {
        // 1. Check verification token
        const tokenDoc = await getDoc(doc(db, "verificationTokens", uid));

        if (!tokenDoc.exists()) {
          setStatus("error");
          setErrorMsg("Verification link not found or already used.");
          return;
        }

        const tokenData = tokenDoc.data();

        if (tokenData.token !== token) {
          setStatus("error");
          setErrorMsg("Invalid verification link.");
          return;
        }

        // Check expiry (24 hours)
        const createdAt = tokenData.createdAt?.toDate();
        if (createdAt) {
          const hoursSinceCreated = (new Date() - createdAt) / (1000 * 60 * 60);
          if (hoursSinceCreated > 24) {
            setStatus("expired");
            setErrorMsg("This link has expired. Please request a new one.");
            return;
          }
        }

        // 2. Create user document in Firestore (this means they're verified!)
        const pendingName = localStorage.getItem("pendingUserName") || "User";

        await setDoc(doc(db, "users", uid), {
          name: pendingName,
          email: tokenData.email,
          role: "user",
          emailVerified: true,
          createdAt: serverTimestamp(),
        });

        // 3. Update Firebase Auth emailVerified status
        try {
          const functions = getFunctions();
          const confirmEmailVerification = httpsCallable(functions, 'confirmEmailVerification');
          await confirmEmailVerification({ uid });
        } catch (err) {
          console.error("Error updating auth verification:", err);
        }

        // 4. Delete verification token
        await deleteDoc(doc(db, "verificationTokens", uid));

        // 5. Clear localStorage
        localStorage.removeItem("pendingUserName");

        setStatus("success");

        // Redirect countdown
        const currentUser = auth.currentUser;
        const isLoggedIn = currentUser && currentUser.uid === uid;

        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              navigate(isLoggedIn ? "/dashboard" : "/login", { replace: true });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
        setErrorMsg("Unable to verify email. Please try again.");
      }
    }

    handleVerification();
  }, [searchParams, navigate]);

  const currentUser = auth.currentUser;
  const uid = searchParams.get("uid");
  const isLoggedIn = currentUser && currentUser.uid === uid;

  return (
    <div className="login-page">
      <div className="login-overlay"></div>

      <div className="login-container" style={{
        background: 'rgba(20, 20, 20, 0.95)',
        padding: '50px 40px',
        maxWidth: '550px'
      }}>
        {status === "verifying" && (
          <>
            <div style={{ fontSize: '72px', textAlign: 'center', marginBottom: '20px' }}>⏳</div>
            <h2 style={{ color: '#ff3366', fontSize: '2rem', textAlign: 'center', marginBottom: '20px' }}>
              Verifying Your Email...
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
              Please wait while we confirm your email address.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div style={{ fontSize: '96px', textAlign: 'center', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#00ff88', fontSize: '2.2rem', textAlign: 'center', marginBottom: '20px' }}>
              Email Verified!
            </h2>
            <p style={{ color: '#fff', textAlign: 'center', fontSize: '1.2rem', marginBottom: '30px' }}>
              🎉 Your account is now fully activated!
            </p>
            <div style={{
              background: 'rgba(0,255,136,0.1)',
              border: '1px solid rgba(0,255,136,0.3)',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              <p style={{ color: '#00ff88', fontSize: '1.1rem', margin: 0 }}>
                Redirecting in {countdown} seconds...
              </p>
            </div>
            <button
              onClick={() => navigate(isLoggedIn ? "/dashboard" : "/login", { replace: true })}
              style={{
                width: '100%',
                padding: '16px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {isLoggedIn ? "Go to Dashboard →" : "Go to Login →"}
            </button>
          </>
        )}

        {status === "expired" && (
          <>
            <div style={{ fontSize: '72px', textAlign: 'center', marginBottom: '20px' }}>⌛</div>
            <h2 style={{ color: '#ff9966', fontSize: '2rem', textAlign: 'center', marginBottom: '20px' }}>
              Link Expired
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: '30px' }}>
              {errorMsg}
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              style={{
                width: '100%',
                padding: '14px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{ fontSize: '72px', textAlign: 'center', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#ff3366', fontSize: '2rem', textAlign: 'center', marginBottom: '20px' }}>
              Verification Failed
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: '30px' }}>
              {errorMsg}
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              style={{
                width: '100%',
                padding: '14px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}